import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseStatementXmlSegments } from './parse';
import { segmentsToSentenceRows } from './rows';
import { StatementXmlPreviewCodesAndSentences } from './StatementXmlPreviewCodesAndSentences';
import { StatementXmlPreviewCodesOnly } from './StatementXmlPreviewCodesOnly';
import { StatementXmlPreviewInline } from './StatementXmlPreviewInline';
import { StatementXmlPreviewTable } from './StatementXmlPreviewTable';
import type { StatementXmlPreviewProps } from './StatementXmlPreview.types';
import type { StatementNodeData } from './types';

export type { StatementXmlPreviewProps } from './StatementXmlPreview.types';

export const StatementXmlPreview: React.FC<StatementXmlPreviewProps> = ({
  value,
  maxLines,
  sx,
  onNodesChange,
  onNodeClick,
  showGlossaryCodeOnHover = true,
  layout = 'inline',
  previewMode,
  dense = false,
  maxVisibleRows = 6,
  showRowNumbers = false,
}) => {
  const { t } = useTranslation();
  const segments = useMemo(() => parseStatementXmlSegments(value), [value]);
  const rows = useMemo(() => segmentsToSentenceRows(segments), [segments]);
  const mode = previewMode ?? layout;
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);

  const nodes = useMemo(
    () => segments.filter((segment): segment is { kind: 'node'; node: StatementNodeData } => segment.kind === 'node').map((s) => s.node),
    [segments]
  );

  useEffect(() => {
    onNodesChange?.(nodes);
  }, [nodes, onNodesChange]);

  if (mode === 'codesAndSentences') {
    return (
      <StatementXmlPreviewCodesAndSentences
        rows={rows}
        sx={sx}
        hoveredCode={hoveredCode}
        onHoveredCodeChange={setHoveredCode}
      />
    );
  }

  if (mode === 'codesOnly') {
    return <StatementXmlPreviewCodesOnly rows={rows} sx={sx} />;
  }

  if (mode === 'table') {
    return (
      <StatementXmlPreviewTable
        rows={rows}
        nodes={nodes}
        dense={dense}
        maxVisibleRows={maxVisibleRows}
        showRowNumbers={showRowNumbers}
        sx={sx}
        showGlossaryCodeOnHover={showGlossaryCodeOnHover}
        onNodeClick={onNodeClick}
        t={t}
      />
    );
  }

  return (
    <StatementXmlPreviewInline
      segments={segments}
      maxLines={maxLines}
      sx={sx}
      showGlossaryCodeOnHover={showGlossaryCodeOnHover}
      onNodeClick={onNodeClick}
      t={t}
    />
  );
};
